import { useState, useRef, useEffect } from 'react'
import { useMutation, useQuery } from 'react-query'
import { chatAPI, chatPromptsAPI } from '../../lib/api'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { LoadingSpinner } from '../LoadingSpinner'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'

interface Message {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: Date
  sources?: any[]
}

export function ChatInterface() {
  const [messages, setMessages] = useState<Message[]>([])
  const [conversationId, setConversationId] = useState<string | undefined>()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [isTyping, setIsTyping] = useState(false)

  // Fetch chat prompts from API
  const { data: prompts = [], isLoading: promptsLoading } = useQuery(
    'chatPromptsPublic',
    chatPromptsAPI.getPrompts,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  )

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const sendMessage = useMutation(
    async (content: string) => {
      // Mobile-specific debugging
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        console.log('[Mobile] Sending message:', { 
          content: content.substring(0, 50), 
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent 
        });
      }
      
      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: new Date(),
      }

      setMessages(prev => [...prev, userMessage])
      setIsTyping(true)

      try {
        const response = await chatAPI.sendMessage(
          [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content,
          })),
          conversationId
        )

        if (isMobile) {
          console.log('[Mobile] Response received:', { 
            hasResponse: !!response, 
            responseLength: response?.response?.length 
          });
        }

        return response
      } catch (error: any) {
        if (isMobile) {
          console.error('[Mobile] API Error Details:', {
            message: error?.message,
            response: error?.response?.data,
            status: error?.response?.status,
            url: error?.config?.url,
            method: error?.config?.method,
          });
        }
        throw error;
      }
    },
    {
      onSuccess: (data) => {
        const assistantMessage: Message = {
          id: Date.now().toString(),
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          sources: data.sources,
        }

        setMessages(prev => [...prev, assistantMessage])
        setConversationId(data.conversation_id)
        setIsTyping(false)
      },
      onError: (error: any) => {
        console.error('Chat error:', error)
        
        // More specific error messages
        let errorMessage = 'Fehler beim Senden der Nachricht';
        if (error?.response?.status === 401) {
          errorMessage = 'Sitzung abgelaufen. Bitte neu anmelden.';
        } else if (error?.response?.status === 500) {
          errorMessage = 'Serverfehler. Bitte versuchen Sie es später erneut.';
        } else if (!navigator.onLine) {
          errorMessage = 'Keine Internetverbindung.';
        } else if (error?.message?.includes('Network')) {
          errorMessage = 'Netzwerkfehler. Bitte Verbindung prüfen.';
        } else if (error?.code === 'ERR_NETWORK') {
          errorMessage = 'Netzwerkfehler. Möglicherweise blockiert Safari die Anfrage.';
        }
        
        toast.error(errorMessage)
        setIsTyping(false)
        
        // Remove the user message on error
        setMessages(prev => prev.slice(0, -1))
      },
    }
  )

  const handleSend = (content: string) => {
    if (content.trim() && !sendMessage.isLoading) {
      sendMessage.mutate(content)
    }
  }

  return (
    <div className="flex flex-col h-full max-h-[calc(100vh-200px)]">
      {/* Welcome message for empty state */}
      {messages.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex items-center justify-center"
        >
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-4">
              Willkommen bei XORA!
            </h2>
            <p className="text-gray-400 mb-8">
              Ich bin dein KI-Assistent für alle Fragen rund um die Pflegehilfe für Senioren.
              Wie kann ich dir heute helfen?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
              {promptsLoading ? (
                // Show loading placeholders while prompts are loading
                [1, 2, 3, 4].map((index) => (
                  <div
                    key={index}
                    className="glass p-3 rounded-lg animate-pulse"
                  >
                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                  </div>
                ))
              ) : prompts.length > 0 ? (
                // Show API prompts if available
                prompts.map((prompt: any, index: number) => (
                  <motion.button
                    key={prompt.id || index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleSend(prompt.prompt_text)}
                    className="glass hover:bg-white/20 p-3 rounded-lg text-sm transition-smooth text-left"
                  >
                    {prompt.prompt_text}
                  </motion.button>
                ))
              ) : (
                // Fallback to default prompts only if API has finished loading but returned no prompts
                [
                  'Welche Möglichkeiten zur Bezuschussung meines Kunden gibt es?',
                  'Welche Reklamationsrichtlinien gibt es?',
                  'Wie kann ich meinen Kunden bei der Pflegegrad-Beantragung unterstützen?',
                  'Was sind die wichtigsten Verkaufsargumente für unsere Dienstleistung?',
                ].map((suggestion, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => handleSend(suggestion)}
                    className="glass hover:bg-white/20 p-3 rounded-lg text-sm transition-smooth text-left"
                  >
                    {suggestion}
                  </motion.button>
                ))
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div className="flex-1 overflow-y-auto space-y-4 mb-4 scrollbar-hide">
          <AnimatePresence>
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
          </AnimatePresence>
          
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-2 text-gray-400"
            >
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="text-sm">XORA denkt nach...</span>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      <div className="mt-6">
        <ChatInput
          onSend={handleSend}
          disabled={sendMessage.isLoading}
          placeholder="Stellen Sie Ihre Frage..."
        />
      </div>
    </div>
  )
}